import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Heart,
  Users,
  Globe,
  ArrowRight,
  Sparkles,
  Check,
  ChevronLeft,
  ChevronRight,
  Link as LinkIcon,
} from "lucide-react";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { useTranslation } from "react-i18next";

const Bullet = ({ children }) => (
  <li className="flex items-start gap-3 mb-3">
    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-warm-sage/20 flex items-center justify-center mt-0.5">
      <Check className="h-3 w-3 text-warm-sage" />
    </div>
    <span className="text-sm text-charcoal/90 leading-relaxed flex-1">
      {children}
    </span>
  </li>
);

// Tastatur-A11y: ganze Karte als Link bedienbar
const handleCardKey = (e, onOpen) => {
  if (e.key === "Enter" || e.key === " ") {
    e.preventDefault();
    onOpen();
  }
};

const DomainsSection = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  const go = (path) =>
    navigate({ pathname: path, search: location.search || "" });

  const domains = [
    {
      icon: Heart,
      title: t("domains.domain1.title"),
      subtitle: t("domains.domain1.subtitle"),
      shortTitle: t("domains.domain1.shortTitle"),
      image:
        "https://images.unsplash.com/photo-1498078675142-85259d452c6b?ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&q=80&w=1160",
      color: "warm-sage",
      badge: "bg-warm-sage/90",
      btn: "bg-warm-sage hover:bg-warm-sage/90 text-cream-white",
      path: "/art-d-etre-soi",
      intro: t("domains.domain1.intro"),
      bullets: [
        t("domains.domain1.bullets.0"),
        t("domains.domain1.bullets.1"),
        t("domains.domain1.bullets.2"),
        t("domains.domain1.bullets.3"),
      ],
      impact: t("domains.domain1.impact"),
      cta: t("domains.domain1.cta"),
    },
    {
      icon: Users,
      title: t("domains.domain2.title"),
      subtitle: t("domains.domain2.subtitle"),
      shortTitle: t("domains.domain2.shortTitle"),
      image:
        "https://images.unsplash.com/photo-1552819289-824d37ca69d2?q=80&w=1738&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
      color: "soft-taupe",
      badge: "bg-soft-taupe/90",
      btn: "bg-soft-taupe hover:bg-soft-taupe/90 text-cream-white",
      path: "/aube-parentale",
      intro: t("domains.domain2.intro"),
      bullets: [
        t("domains.domain2.bullets.0"),
        t("domains.domain2.bullets.1"),
        t("domains.domain2.bullets.2"),
        t("domains.domain2.bullets.3"),
      ],
      impact: t("domains.domain2.impact"),
      cta: t("domains.domain2.cta"),
    },
    {
      icon: Globe,
      title: t("domains.domain3.title"),
      subtitle: t("domains.domain3.subtitle"),
      shortTitle: t("domains.domain3.shortTitle"),
      image:
        "https://images.unsplash.com/photo-1504150558240-0b4fd8946624?ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&q=80&w=928",
      color: "rich-chocolate",
      badge: "bg-rich-chocolate/90",
      btn: "bg-rich-chocolate hover:bg-rich-chocolate/90 text-cream-white",
      path: "/architecte-ancrage",
      intro: t("domains.domain3.intro"),
      bullets: [
        t("domains.domain3.bullets.0"),
        t("domains.domain3.bullets.1"),
        t("domains.domain3.bullets.2"),
        t("domains.domain3.bullets.3"),
      ],
      impact: t("domains.domain3.impact"),
      cta: t("domains.domain3.cta"),
    },
  ];

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const nextSlide = () =>
    setCurrentIndex((p) => (p === domains.length - 1 ? 0 : p + 1));
  const prevSlide = () =>
    setCurrentIndex((p) => (p === 0 ? domains.length - 1 : p - 1));
  const goToSlide = (i) => setCurrentIndex(i);

  return (
    <section
      id="domaines"
      className="py-10 md:py-10 bg-gradient-to-b from-cream-white to-soft-gray/20 relative overflow-hidden"
    >
      {/* Fond décoratif */}
      <div className="absolute inset-0 pointer-events-none opacity-5">
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-warm-sage/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-48 h-48 bg-soft-taupe/10 rounded-full blur-3xl animate-pulse delay-1000" />
      </div>

      {/* En-tête */}
      <div className="text-center mb-12 md:mb-20 relative z-10 px-4">
        <div className="flex justify-center items-center mb-6">
          <div className="h-px w-12 md:w-20 bg-gradient-to-r from-transparent via-warm-sage to-transparent" />
          <div className="mx-3 md:mx-4 p-2 bg-warm-sage/10 rounded-full border border-warm-sage/20">
            <Sparkles className="h-5 w-5 md:h-6 md:w-6 text-warm-sage" />
          </div>
          <div className="h-px w-12 md:w-20 bg-gradient-to-r from-transparent via-warm-sage to-transparent" />
        </div>
        <h2 className="text-3xl md:text-5xl font-serif font-light text-charcoal mb-4 md:mb-6 leading-tight">
          {t("domains.title")}
        </h2>
        <p className="text-warm-gray text-base md:text-xl mt-4 font-light max-w-2xl md:max-w-3xl mx-auto leading-relaxed">
          {t("domains.subtitle")}
        </p>
      </div>

      {/* Desktop : 3 cartes */}
      <div className="hidden md:grid md:grid-cols-3 gap-6 lg:gap-8 max-w-7xl mx-auto px-6 relative z-10">
        {domains.map((d, idx) => {
          const Icon = d.icon;
          return (
            <Card
              key={idx}
              role="link"
              tabIndex={0}
              onClick={() => go(d.path)}
              onKeyDown={(e) => handleCardKey(e, () => go(d.path))}
              aria-label={`${d.title} – ouvrir la page du service`}
              className="group relative bg-white rounded-2xl overflow-hidden shadow-soft hover:shadow-xl transition-all duration-500 border border-soft-gray/20 h-full flex flex-col transform hover:-translate-y-2 cursor-pointer focus:outline-none focus:ring-2 focus:ring-warm-sage/40"
            >
              {/* Full-card invisible link area for extra clarity */}
              <span className="absolute inset-0 z-10" aria-hidden="true" />

              {/* Top-right link hint (non text-breaking) */}
              <div className="absolute top-3 right-3 z-20">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/85 backdrop-blur border border-white/60 text-xs text-charcoal/80 shadow-sm">
                  <LinkIcon className="h-3.5 w-3.5" />
                  <ArrowRight className="h-3.5 w-3.5" />
                </span>
              </div>

              {/* Image */}
              <div className="relative h-48 overflow-hidden">
                <img
                  src={d.image}
                  alt={d.title}
                  className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700 ease-out"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />

                {/* Floating service ribbon (keine Textänderung am Content) */}
                <div
                  className={`absolute top-4 left-4 ${d.badge} p-3 rounded-xl shadow-lg backdrop-blur-sm`}
                >
                  <Icon className="h-5 w-5 text-cream-white" />
                </div>

                {/* Hover CTA overlay (nur Icon + animierter Pfeil) */}
                <div className="pointer-events-none absolute inset-0 flex items-end justify-end p-3">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/45 text-cream-white text-[11px] backdrop-blur-sm">
                      <ArrowRight className="h-3.5 w-3.5 translate-x-0 group-hover:translate-x-0.5 transition-transform" />
                    </div>
                  </div>
                </div>

                <div className="absolute bottom-4 left-4 right-4">
                  <h3 className="text-xl font-serif font-semibold text-cream-white mb-1">
                    {d.title}
                  </h3>
                  <p className="text-cream-white/90 text-sm font-light">
                    {d.subtitle}
                  </p>
                </div>
              </div>

              {/* Contenu */}
              <div className="p-6 flex-1 flex flex-col">
                <p className="text-charcoal/80 text-sm leading-relaxed mb-4 italic border-l-2 border-warm-sage/30 pl-3">
                  {d.intro}
                </p>
                <ul className="mb-6 flex-1">
                  {d.bullets.map((b, i) => (
                    <Bullet key={i}>{b}</Bullet>
                  ))}
                </ul>

                <div className="mt-auto pt-4 border-t border-soft-gray/20">
                  <p className="text-sm text-warm-gray/90 italic font-light leading-relaxed mb-4">
                    {d.impact}
                  </p>

                  {/* Button bleibt – zusätzlich zeigt die ganze Karte auf die Unterseite */}
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      go(d.path);
                    }}
                    className={`w-full ${d.btn} py-3 rounded-xl font-medium shadow-md hover:shadow-lg transition-all duration-300 group/btn flex items-center justify-center gap-2`}
                  >
                    <span>{d.cta}</span>
                    <ArrowRight className="h-4 w-4 transition-transform group-hover/btn:translate-x-1" />
                  </Button>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Mobile : carrousel 1 carte (nur Mini-Hinweis oben rechts, Rest identisch) */}
      <div className="md:hidden relative max-w-sm mx-auto px-4 z-10">
        {/* Onglets rapides */}
        <div className="mb-6">
          <div className="grid grid-cols-3 gap-2">
            {domains.map((d, i) => (
              <button
                key={i}
                onClick={() => goToSlide(i)}
                className={`p-3 rounded-xl text-center transition-all duration-300 border-2 ${
                  i === currentIndex
                    ? "bg-warm-sage/10 border-warm-sage text-warm-sage shadow-md"
                    : "bg-white/80 border-soft-gray/30 text-charcoal/70 hover:bg-white hover:border-warm-sage/50"
                }`}
              >
                <div className="flex flex-col items-center">
                  <d.icon
                    className={`h-4 w-4 mb-1 ${
                      i === currentIndex ? "text-warm-sage" : "text-charcoal/50"
                    }`}
                  />
                  <span className="text-xs font-medium leading-tight">
                    {d.shortTitle}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Piste */}
        <div className="relative overflow-hidden rounded-2xl">
          <div
            className="flex transition-transform duration-500 ease-out"
            style={{ transform: `translateX(-${currentIndex * 100}%)` }}
          >
            {domains.map((d, i) => {
              const Icon = d.icon;
              return (
                <div key={i} className="w-full flex-shrink-0 px-2">
                  <Card
                    className="bg-white rounded-2xl overflow-hidden shadow-lg border border-soft-gray/20"
                    role="link"
                    tabIndex={0}
                    onClick={() => go(d.path)}
                    onKeyDown={(e) => handleCardKey(e, () => go(d.path))}
                    aria-label={`${d.title} – ouvrir la page du service`}
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={d.image}
                        alt={d.title}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                      <div
                        className={`absolute top-4 left-4 ${d.badge} p-3 rounded-xl shadow-lg`}
                      >
                        <Icon className="h-5 w-5 text-cream-white" />
                      </div>

                      {/* kleiner Link-Hinweis */}
                      <div className="absolute top-3 right-3 z-20">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/85 backdrop-blur border border-white/60 text-[10px] text-charcoal/80">
                          <LinkIcon className="h-3 w-3" />
                          <ArrowRight className="h-3 w-3" />
                        </span>
                      </div>

                      <div className="absolute bottom-4 left-4 right-4">
                        <h3 className="text-lg font-serif font-semibold text-cream-white mb-1">
                          {d.title}
                        </h3>
                        <p className="text-cream-white/90 text-xs font-light">
                          {d.subtitle}
                        </p>
                      </div>
                    </div>

                    <div className="p-5">
                      <p className="text-charcoal/80 text-sm leading-relaxed mb-3 italic border-l-2 border-warm-sage/30 pl-3">
                        {d.intro}
                      </p>
                      <ul className="mb-4">
                        {d.bullets.map((b, j) => (
                          <Bullet key={j}>{b}</Bullet>
                        ))}
                      </ul>

                      <div className="pt-3 border-t border-soft-gray/20">
                        <p className="text-xs text-warm-gray/90 italic font-light leading-relaxed mb-3">
                          {d.impact}
                        </p>
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            go(d.path);
                          }}
                          className={`w-full ${d.btn} py-3 rounded-xl text-sm font-medium shadow-md`}
                        >
                          {d.cta}
                          <ArrowRight className="ml-2 h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </Card>
                </div>
              );
            })}
          </div>
        </div>

        {/* Contrôles */}
        <div className="flex justify-center items-center mt-6 space-x-4">
          <button
            onClick={prevSlide}
            className="p-3 rounded-full bg-white/80 shadow-lg border border-soft-gray/20 hover:bg-white transition-colors"
          >
            <ChevronLeft className="h-4 w-4 text-charcoal" />
          </button>
          <div className="flex space-x-2">
            {domains.map((_, i) => (
              <button
                key={i}
                onClick={() => goToSlide(i)}
                className={`w-3 h-3 rounded-full transition-all ${
                  i === currentIndex
                    ? "bg-warm-sage scale-110"
                    : "bg-soft-gray hover:bg-warm-sage/50"
                }`}
              />
            ))}
          </div>
          <button
            onClick={nextSlide}
            className="p-3 rounded-full bg-white/80 shadow-lg border border-soft-gray/20 hover:bg-white transition-colors"
          >
            <ChevronRight className="h-4 w-4 text-charcoal" />
          </button>
        </div>
      </div>

      {/* Citation */}
      <div className="text-center mt-12 md:mt-16 max-w-2xl mx-auto px-6">
        <div className="bg-gradient-to-r from-warm-sage/5 to-soft-taupe/5 rounded-2xl p-6 md:p-8 border border-soft-gray/20">
          <p className="text-sm md:text-base text-charcoal/90 font-light italic leading-relaxed">
            {t("domains.quote")}
          </p>
        </div>
      </div>
    </section>
  );
};

export default DomainsSection;
